import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Avatar, IconButton, CircularProgress, Button, TextField, Divider, AppBar, useMediaQuery, Skeleton } from '@mui/material';
import { styled, useTheme } from '@mui/system';
import { Settings, Star, AccountCircle, Edit, Menu, Home, AddAPhoto } from '@mui/icons-material';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { sendPasswordResetEmail, deleteUser } from 'firebase/auth';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { auth, db, storage } from '../firebaseConfig';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 240;

// Contenu principal qui s'étend pour occuper tout l'espace disponible
const MainContent = styled(Box)(({ theme }) => ({
    padding: '2rem',
    width: '100%',
    justifyContent: 'center',
    backgroundColor: '#141414',
    color: 'white',
    minHeight: '100vh',
    [theme.breakpoints.down('md')]: {
        padding: '1rem',
    },
}));

// Cartes pour les informations importantes
const InfoCard = styled(Box)(({ theme }) => ({
    padding: '1.5rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#1e1e1e',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    borderRadius: '15px',
    color: 'white',
}));

const SidebarIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '1rem 2rem',
    width: '100%',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#1f1f1f',
    },
}));

function ProfilComponent() {
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [profileImage, setProfileImage] = useState(null); // Image de profil actuelle
    const [bannerProfile, setBannerProfile] = useState(null); // Bannière de profile actuelle
    const [progress, setProgress] = useState(0); // Progression du téléchargement de l'image de profil
    const [progressBanner, setProgressBanner] = useState(0); // Progression du téléchargement de la bannière
    const [hoverProfile, setHoverProfile] = useState(false);
    const [hoverBanner, setHoverBanner] = useState(false);
    const [selectedSection, setSelectedSection] = useState('profile'); // Section sélectionnée
    const [stats, setStats] = useState({
        favorite: 0,
        watched: 0,
        inProgress: 0,
        toWatch: 0,
    });
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    useEffect(() => {
        const user = auth.currentUser;
        if (user) {
            setEmail(user.email);
            const fetchUserData = async () => {
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setUsername(userData.username);
                    setProfileImage(userData.profileImageURL);
                    setBannerProfile(userData.bannerProfileURL);
                    setStats({
                        favorite: userData.favoriteKdramas?.length || 0,
                        watched: userData.watchedKdramas?.length || 0,
                        inProgress: userData.inProgressKdramas?.length || 0,
                        toWatch: userData.favoriteKdramas?.length || 0,
                    });
                }
            };
            fetchUserData();
        }
    }, []);

    const handleDrawerToggle = () => {
        setDrawerOpen(!drawerOpen);
    }

    // Gérer la mise à jour du profil
    const handleUpdateProfile = async () => {
        const user = auth.currentUser;
        if (user) {
            await updateDoc(doc(db, 'users', user.uid), { username, email });
            console.log('Profil mis à jour avec succès');
        }
    };

    // Gérer le changement d'image de profil
    const handleProfileImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const storageRef = ref(storage, `profileImages/${auth.currentUser.uid}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setProgress(progress); // Mettre à jour la progression
            },
            (error) => {
                console.error('Erreur lors du téléchargement de l\'image:', error);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                setProfileImage(downloadURL);
                setProgress(0); // Réinitialiser la progression une fois terminé
                await updateDoc(doc(db, 'users', auth.currentUser.uid), { profileImageURL: downloadURL });
            }
        );
    };

    // Gérer le changement de bannière de profil
    const handleBannerProfileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const storageRef = ref(storage, `bannerProfile/${auth.currentUser.uid}/${file.name}`);
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTask.on(
            'state_changed',
            (snapshot) => {
                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setProgressBanner(progress);
            },
            (error) => {
                console.error('Erreur lors du déchargement de l\'image:', error);
            },
            async () => {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                setBannerProfile(downloadURL);
                setProgressBanner(0); // Réinitialiser la progression une fois terminé
                await updateDoc(doc(db, 'users', auth.currentUser.uid), { bannerProfileURL: downloadURL });
            }
        );
    };

    // Réinitialisation du mot de passe
    const handlePasswordReset = async () => {
        if (auth.currentUser && auth.currentUser.email) {
            await sendPasswordResetEmail(auth, auth.currentUser.email);
            alert('Un email pour réinitialiser votre mot de passe a été envoyé.');
        }
    };

    // Suppression du compte et des données
    const handleDeleteAccount = async () => {
        const user = auth.currentUser;
        if (user) {
            try {
                await deleteDoc(doc(db, 'users', user.uid));
                await deleteUser(user);
                alert('Votre compte et vos données ont été supprimés avec succès.');
            } catch (error) {
                console.error('Erreur lors de la suppression du compte :', error);
                alert('Erreur lors de la suppression du compte.');
            }
        }
    };

    const navToHome = () => {
        navigate('/');
    }

    const renderSection = () => {
        switch (selectedSection) {
            case 'profile':
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <InfoCard>
                                <Typography variant="h5" sx={{ fontFamily: 'SUSE, sans-serif' }}>Mon Profil</Typography>
                                <TextField
                                    label="Nom d'utilisateur"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    fullWidth
                                    sx={{
                                        mt: 2,
                                        '& .MuiInputBase-root': { color: 'white' },
                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#555',
                                        },
                                        '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#888',
                                        },
                                        '& .MuiInputLabel-root': { color: '#aaa' },
                                    }}
                                />
                                <TextField
                                    label="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    fullWidth
                                    sx={{
                                        mt: 2,
                                        mb: 2,
                                        '& .MuiInputBase-root': { color: 'white' },
                                        '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#555',
                                        },
                                        '&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#888',
                                        },
                                        '& .MuiInputLabel-root': { color: '#aaa' },
                                    }}
                                />
                                <Button variant="contained" color="primary" onClick={handleUpdateProfile} sx={{ fontFamily: 'SUSE, sans-serif' }}>
                                    Mettre à jour
                                </Button>
                            </InfoCard>
                        </Grid>
                    </Grid>
                );

            case 'favorites':
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <InfoCard>
                                <Typography variant="h5">K-Dramas favoris</Typography>
                                <Typography variant="h6">{stats.favorite} K-Dramas</Typography>
                            </InfoCard>
                        </Grid>
                        <Grid item xs={12}>
                            <InfoCard>
                                <Typography variant="h5">K-Dramas Vus</Typography>
                                <Typography variant="h6">{stats.watched} K-Dramas</Typography>
                            </InfoCard>
                        </Grid>
                        <Grid item xs={12}>
                            <InfoCard>
                                <Typography variant="h5">K-Dramas en Cours</Typography>
                                <Typography variant="h6">{stats.inProgress} K-Dramas</Typography>
                            </InfoCard>
                        </Grid>
                        <Grid item xs={12}>
                            <InfoCard>
                                <Typography variant="h5">K-Dramas à Voir</Typography>
                                <Typography variant="h6">{stats.toWatch} K-Dramas</Typography>
                            </InfoCard>
                        </Grid>
                    </Grid>
                );

            case 'settings':
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <InfoCard>
                                <Typography variant="h5">Paramètres</Typography>
                                <Button variant="contained" color="primary" onClick={handlePasswordReset} sx={{ marginTop: '1rem' }}>
                                    Réinitialiser le mot de passe
                                </Button>
                                <Button variant="contained" color="error" onClick={handleDeleteAccount} sx={{ marginTop: '1rem', backgroundColor: '#d32f2f' }}>
                                    Supprimer mon compte
                                </Button>
                            </InfoCard>
                        </Grid>
                    </Grid>
                );

            default:
                return null;
        }
    };

    return (
        <Box sx={{ display: 'flex', mb: 2 }}>

            <AppBar position="fixed" sx={{ backgroundColor: 'rgb(37, 150, 190)', width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` } }}>
                <Toolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { sm: 'none' } }}
                    >
                        <Menu />
                    </IconButton>
                    <Typography onClick={navToHome} sx={{ fontFamily: 'SUSE, sans-serif', cursor: 'pointer', textAlign: isMobile ? 'center' : 'left', '&:hover': { textDecoration: isMobile ? 'none' : 'underline' } }} variant="h6" >
                        K-Drama Tracker
                    </Typography>
                </Toolbar>
            </AppBar>

            {/* Drawer permanent pour la sidebar */}
            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={handleDrawerToggle}
                variant={isMobile ? 'temporary' : 'permanent'}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', backgroundColor: '#121212' },
                }}
            >
                <Toolbar />
                <Box sx={{ overflow: 'auto' }}>

                    <SidebarIcon onClick={navToHome}>
                        <Home sx={{ color: 'white', marginRight: '1rem' }} />
                        <Typography sx={{ color: 'white' }}>Accueil</Typography>
                    </SidebarIcon>
                    <SidebarIcon onClick={() => setSelectedSection('profile')}>
                        <AccountCircle sx={{ color: 'white', marginRight: '1rem' }} />
                        <Typography sx={{ color: 'white' }}>Mon Profil</Typography>
                    </SidebarIcon>
                    <SidebarIcon onClick={() => setSelectedSection('favorites')}>
                        <Star sx={{ color: 'white', marginRight: '1rem' }} />
                        <Typography sx={{ color: 'white' }}>Favoris</Typography>
                    </SidebarIcon>
                    <SidebarIcon onClick={() => setSelectedSection('settings')}>
                        <Settings sx={{ color: 'white', marginRight: '1rem' }} />
                        <Typography sx={{ color: 'white' }}>Paramètres</Typography>
                    </SidebarIcon>
                    <Box sx={{ textAlign: 'center' }}>
                        <Button variant="contained" color="secondary" sx={{ marginTop: '1rem', width: '90%' }} onClick={() => auth.signOut()}>
                            Déconnexion
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            {/* Contenu principal */}
            <MainContent>
                <Typography variant="h4" sx={{ marginBottom: '2rem', marginTop: '3rem', fontFamily: 'SUSE, sans-serif' }}>
                    Bienvenue, {username}
                </Typography>


                <Box
                    sx={{
                        position: 'relative',
                        marginBottom: '2rem',
                        borderRadius: '10px',
                        width: '100%',
                        maxHeight: '500px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                    }}
                    onMouseEnter={() => setHoverBanner(true)}
                    onMouseLeave={() => setHoverBanner(false)}
                >
                    {bannerProfile ? (
                        <img
                            src={bannerProfile}
                            alt="banner"
                            style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: '10px',
                                objectFit: 'cover',
                            }}
                        />
                    ) : (
                        <Skeleton
                            variant="rectangular"
                            width="100%"
                            height="350px"
                            animation="wave"
                            sx={{
                                borderRadius: '10px',
                                backgroundColor: '#e0e0e0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >

                        </Skeleton>
                    )}

                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: 20,
                            right: 20,
                            display: { xs: 'block', md: hoverBanner ? 'block' : 'none' },
                        }}
                        component="label"
                    >
                        <Edit sx={{ color: 'white' }} />
                        <input accept="image/*" type="file" style={{ display: 'none' }} onChange={handleBannerProfileChange} />
                    </IconButton>

                    {progressBanner > 0 && (
                        <CircularProgress
                            size={40}
                            sx={{
                                position: 'absolute',
                                bottom: 20,
                                left: 20,
                                color: 'white',
                            }}
                            variant="determinate"
                            value={progressBanner}
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        position: 'relative',
                        marginBottom: '20px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        padding: '1rem',
                    }}
                >
                    <Avatar
                        src={profileImage || ''}
                        sx={{ width: 120, height: 120, margin: 'auto' }}
                        onMouseEnter={() => setHoverProfile(true)}
                        onMouseLeave={() => setHoverProfile(false)}
                    >
                        {!profileImage && username.charAt(0).toUpperCase()}
                    </Avatar>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 60,
                            left: 60,
                            display: { xs: 'block', md: hoverProfile ? 'block' : 'none' },
                        }}
                        component="label"
                    >
                        <Edit sx={{ color: 'white' }} />
                        <input accept="image/*" type="file" style={{ display: 'none' }} onChange={handleProfileImageChange} />
                    </IconButton>
                    {progress > 0 && (
                        <CircularProgress
                            size={40}
                            sx={{
                                position: 'absolute',
                                bottom: 10,
                                left: 10,
                                color: 'white',
                            }}
                            variant="determinate"
                            value={progress}
                        />
                    )}
                </Box>
                {renderSection()}
            </MainContent>
        </Box>
    );
}

export default ProfilComponent;
